import type { BrandReport, ReportFile, ReportProof } from "@talentkontor/domain";

/**
 * Nachweise für das PDF des Berichts (0073): jede Datei der Beiträge einmal laden (über den Link bzw. die Vorschau des Teams),
 * Prüfwert der hochgeladenen Datei bilden und Bilder (PNG, JPEG, WebP) als JPEG auf weißem Grund für das PDF umwandeln
 * (höchstens 1600 Pixel an der langen Seite). PDF-Nachweise stehen mit Dateiname und Prüfwert im Anhang. Eine Datei, die sich
 * nicht laden lässt, fehlt im Anhang; das PDF entsteht trotzdem.
 */

const BILD = ["image/png", "image/jpeg", "image/webp"];
const MAX_SEITE = 1600;

export async function sha256Hex(bytes: ArrayBuffer): Promise<string> {
  const buf = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(buf), (b) => b.toString(16).padStart(2, "0")).join("");
}

async function alsJpeg(blob: Blob): Promise<ReportProof["image"]> {
  if (typeof document === "undefined" || typeof createImageBitmap === "undefined") return null;
  try {
    const bmp = await createImageBitmap(blob);
    const scale = Math.min(1, MAX_SEITE / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bmp.width * scale));
    canvas.height = Math.max(1, Math.round(bmp.height * scale));
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    bmp.close();
    const jpeg = await new Promise<Blob | null>((res) => canvas.toBlob(res, "image/jpeg", 0.85));
    if (!jpeg) return null;
    return { jpeg: new Uint8Array(await jpeg.arrayBuffer()), width: canvas.width, height: canvas.height };
  } catch {
    return null;
  }
}

export async function berichtNachweise(report: Pick<BrandReport, "items">, load: (f: ReportFile) => Promise<Blob>): Promise<Record<string, ReportProof>> {
  const out: Record<string, ReportProof> = {};
  for (const f of report.items.flatMap((it) => it.files)) {
    try {
      const blob = await load(f);
      const bytes = await blob.arrayBuffer();
      out[f.id] = { sha256: await sha256Hex(bytes), image: BILD.includes(f.mime) ? await alsJpeg(new Blob([bytes], { type: f.mime })) : null };
    } catch {
      // ohne diese Datei weiter; im PDF steht dann der Hinweis auf den Link
    }
  }
  return out;
}
